import React, { useCallback, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemIcon, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import TagsDropdown from '../TagsDropdown';
import { useWebSocket } from '../WebSocketContext';
import { useDashboard } from '../Dashboard/DashboardContext';
import { systemTagsEvent, systemTagsGlobal } from '../../constants/systemTags';
import TagPreviewPopover from '../Common/TagPreviewPopover';
import { findCrossScopeTagValues, findReservedUserTagValues, hasUserTagVariableReservedChars, normalizeTagVariable } from '../../Utils/tagUtils';
import { getStorageFileName, getStorageReferenceLabel } from '../Common/storageMentionUtils';
import { hasFileDragData } from '../../Utils/fileDropUtils';

const escapeHtml = (text) => {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

// A file-looking @ reference that was restored as plain text is not safe to
// submit as a storage input. Keep it visible, but make the missing attachment
// explicit so the user can select the authorized file before saving.
const unresolvedStorageMentionPattern = /@[^@\n]+?\s\/\s[^@\n/]+?\.[A-Za-z0-9]{1,12}(?=$|[\s.,;:!?])/g;

const getTagValueForLookup = (tag) => {
  if (!tag) return '';
  const candidate = tag?.variable ?? tag?.value ?? tag?.key ?? tag?.name ?? tag?.label ?? tag;
  if (candidate == null) return '';
  return normalizeTagVariable(candidate);
};

const getTagLookupCandidates = (tag) => {
  if (!tag) return [];
  return [
    tag?.variable,
    tag?.value,
    tag?.key,
    tag?.name,
    tag?.label
  ]
    .filter((candidate) => typeof candidate === 'string' && candidate.trim().length > 0)
    .map((candidate) => normalizeTagVariable(candidate));
};

const standaloneTagPattern = /^\s*(\{\{([^{}\s]+)\}\}|\{([^{}\s]+)\})\s*$/;

const escapeRegExp = (text) => String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const trimStandalonePastedTag = (text) => {
  const matchableText = String(text || '').replace(/\u00A0/g, ' ');
  return standaloneTagPattern.test(matchableText) ? matchableText.trim() : text;
};

const getStandalonePastedTag = (text) => {
  const matchableText = String(text || '').replace(/\u00A0/g, ' ');
  const match = matchableText.match(standaloneTagPattern);
  if (!match) return null;
  return {
    tagName: match[2] || match[3],
    escaped: Boolean(match[2])
  };
};

const normalizeTagsInput = (tagsInput) => {
  if (!tagsInput) return [];
  if (typeof tagsInput === 'string') {
    try {
      const parsed = JSON.parse(tagsInput);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      return [tagsInput];
    }
  }
  if (Array.isArray(tagsInput)) {
    return tagsInput;
  }
  if (typeof tagsInput === 'object') {
    return [tagsInput];
  }
  return [];
};

const mergeTagDefinitions = (...tagGroups) => {
  const mergedTags = [];
  const seenValues = new Set();

  tagGroups.forEach((group) => {
    normalizeTagsInput(group).forEach((tag) => {
      const tagValue = getTagValueForLookup(tag);
      const normalizedValue = tagValue.toLowerCase();

      if (normalizedValue && !seenValues.has(normalizedValue)) {
        seenValues.add(normalizedValue);
        mergedTags.push(tag);
        return;
      }

      if (!normalizedValue) {
        mergedTags.push(tag);
      }
    });
  });

  return mergedTags;
};

const TagEditor = forwardRef(({
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  value = '',
  onChange,
  onKeyDown,
  onStorageMentionTrigger,
  onStorageMentionRemove,
  onStorageFilesDrop,
  storageReferences = [],
  placeholder = '',
  onTagClick,
  tags,
  includeDashboardTags = true,
  includeSystemTags = true,
  referenceOnly = false,
  scope,
  caseid,
  className = '',
  singleLine,
  multiline = true,
  disabled = false,
  active = true,
  dense = false,
  minHeight,
  minRows,
  maxRows
}, ref) => {
  const rootRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingUploadRef = useRef(false);
  const dragDepthRef = useRef(0);
  const [fileDragActive, setFileDragActive] = useState(false);
  const [attachmentError, setAttachmentError] = useState(false);
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const dashboard = useDashboard();
  const dashboardTags = dashboard?.tags;
  const [tagMenuAnchorEl, setTagMenuAnchorEl] = useState(null);
  const [triggeredByBrace, setTriggeredByBrace] = useState(false);
  const [tagActionAnchorEl, setTagActionAnchorEl] = useState(null);
  const [tagActionTarget, setTagActionTarget] = useState(null);
  const [escapeDialogOpen, setEscapeDialogOpen] = useState(false);
  const [escapeTarget, setEscapeTarget] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [missingTagDialogOpen, setMissingTagDialogOpen] = useState(false);
  const [missingTagValue, setMissingTagValue] = useState('');
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [hoveredTag, setHoveredTag] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);
  const preserveBraceSelectionRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const tagPreviewCacheRef = useRef(new Map());
  useEffect(() => {
    if (active && !disabled) return;
    setTagMenuAnchorEl(null);
    setTagActionAnchorEl(null);
    setEscapeDialogOpen(false);
    setRenameDialogOpen(false);
    setMissingTagDialogOpen(false);
    setHoverAnchorEl(null);
    setHoveredTag(null);
  }, [active, disabled]);
  const savedSelectionRef = useRef(null);
  const tagDefinitionSignatureRef = useRef('');
  const storageChipPathsRef = useRef(new Map());
  const latestEditorApiRef = useRef({});
  const isSingleLine = typeof singleLine === 'boolean' ? singleLine : !multiline;
  const isDisabled = Boolean(disabled);
  const tagNamePattern = /^[^{}\s]+$/;
  const normalizeForMode = (text) => {
    if (!text) return '';
    if (!isSingleLine) return text;
    return String(text).replace(/\s*\r?\n+\s*/g, ' ');
  };
  const isBlankText = (text) => {
    if (!text) return true;
    return String(text).replace(/\u00A0/g, ' ').trim().length === 0;
  };
  const hasVisibleEditorContent = (root, text) => {
    if (!isBlankText(text)) {
      return true;
    }
    return Boolean(root?.querySelector?.('.tag-chip'));
  };
  const normalizedValue = normalizeForMode(value || '');
  const displayValue = isBlankText(normalizedValue) ? '' : normalizedValue;
  const [isEmpty, setIsEmpty] = useState(isBlankText(displayValue));
  const computedMinHeight = minHeight || (minRows ? `calc(${minRows} * 1.5em + 1.25rem)` : (isSingleLine ? '40px' : '120px'));
  const computedMaxHeight = maxRows ? `calc(${maxRows} * 1.5em + 1.25rem)` : undefined;
  const editorPadding = dense ? 0.5 : 1.25;
  const caretMarker = '__TAG_EDITOR_CARET__';
  const tagPattern = /\{[^{}\s]+\}/;
  const tagRegex = /\{\{([^{}\s]+)\}\}|\{([^{}\s]+)\}/g;
  const normalizedTags = useMemo(
    () => mergeTagDefinitions(tags, includeDashboardTags ? dashboardTags : []),
    [tags, dashboardTags, includeDashboardTags]
  );
  const storageMentionEntries = useMemo(() => {
    const references = Array.isArray(storageReferences) ? storageReferences : [];
    const fileNameCounts = new Map();
    references.forEach((reference) => {
      const path = String(reference?.path || '').trim();
      if (!path) return;
      const fileName = getStorageFileName(path);
      fileNameCounts.set(fileName, (fileNameCounts.get(fileName) || 0) + 1);
    });

    const entries = [];
    references.forEach((reference) => {
      const path = String(reference?.path || '').trim();
      if (!path) return;
      const fileName = getStorageFileName(path);
      const displayLabel = getStorageReferenceLabel(path);
      const storedLabel = String(reference?.label || '').trim();
      const labels = new Set([displayLabel]);
      if (storedLabel) labels.add(storedLabel);
      if (fileNameCounts.get(fileName) === 1) labels.add(fileName);
      labels.forEach((label) => {
        if (!label) return;
        entries.push({ path, label, displayLabel });
      });
    });

    return entries.sort((left, right) => right.label.length - left.label.length);
  }, [storageReferences]);
  const storageMentionLookupSignature = storageMentionEntries
    .map((entry) => `${entry.path}:${entry.label}`)
    .join('|');
  const crossScopeTagValues = useMemo(() => (
    findCrossScopeTagValues(
      [...normalizeTagsInput(tags), ...normalizeTagsInput(includeDashboardTags ? dashboardTags : [])],
      systemTagsEvent
    )
  ), [tags, dashboardTags, includeDashboardTags]);
  const reservedTagCollisions = useMemo(() => findReservedUserTagValues(
    [...normalizeTagsInput(tags), ...normalizeTagsInput(includeDashboardTags ? dashboardTags : [])]
  ), [tags, dashboardTags, includeDashboardTags]);
  const availableTagValueSet = useMemo(() => {
    const set = new Set();
    normalizedTags.forEach((tag) => {
      getTagLookupCandidates(tag).forEach((candidate) => {
        set.add(candidate.toLowerCase());
      });
    });
    [...systemTagsEvent, ...systemTagsGlobal].forEach((tag) => {
      getTagLookupCandidates(tag).forEach((candidate) => {
        set.add(candidate.toLowerCase());
      });
    });
    return set;
  }, [normalizedTags]);
  const hasTagDefinitions = availableTagValueSet.size > 0;
  const isTagMissing = (tagName) => {
    if (!hasTagDefinitions || !tagName) return false;
    return !availableTagValueSet.has(normalizeTagVariable(tagName).toLowerCase());
  };
  const isTagCrossScope = (tagName) => (
    Boolean(tagName) && (crossScopeTagValues.has(normalizeTagVariable(tagName).toLowerCase())
      || reservedTagCollisions.has(normalizeTagVariable(tagName).toLowerCase()))
  );
  const getMissingTagTooltip = useCallback(() => (
    t('tag_editor.missing_tag_warning', 'Problem: this tag does not exist. The system will treat it as empty.')
  ), [t]);
  const getCrossScopeTagTooltip = useCallback((tagName) => (
    reservedTagCollisions.has(normalizeTagVariable(tagName).toLowerCase())
      ? t('tag_names.existing_reserved_name', { name: tagName })
      : t('tag_editor.cross_scope_tag_warning', 'Warning: this tag exists as both a widget and an event tag. Its value depends on the execution scope.')
  ), [reservedTagCollisions, t]);
  const tagLookupSignature = useMemo(() => {
    const values = [...availableTagValueSet];
    values.sort();
    const crossScopeValues = [...crossScopeTagValues, ...reservedTagCollisions].sort();
    return `${values.join('|')}::${crossScopeValues.join('|')}`;
  }, [availableTagValueSet, crossScopeTagValues, reservedTagCollisions]);

  const buildTagSpanHtml = (tagName, escaped = false) => {
    const bgColor = generatePastelColor(tagName);
    const color = generateSaturatedColor(tagName);
    const style = escaped
      ? `background-color: transparent; color: ${color}; border: 1px dashed ${color};`
      : `background-color: ${bgColor}; color: ${color};`;
    const escapedAttr = escaped ? ' data-escaped="true"' : '';
    const className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    const missing = !escaped && isTagMissing(tagName);
    const crossScope = !escaped && !missing && isTagCrossScope(tagName);
    const missingAttr = missing ? ' data-missing-tag="true"' : '';
    const crossScopeAttr = crossScope ? ' data-cross-scope-tag="true"' : '';
    const warningMessage = missing
      ? escapeHtml(getMissingTagTooltip(tagName))
      : (crossScope ? escapeHtml(getCrossScopeTagTooltip(tagName)) : '');
    const labelHtml = `<span class="tag-chip-label">${escapeHtml(tagName)}</span>`;
    const warningHtml = missing || crossScope
      ? `<span class="tag-chip-warning${crossScope ? ' tag-chip-warning-cross-scope' : ''}" aria-label="${warningMessage}" data-warning-tooltip="${warningMessage}" title="${warningMessage}">!</span>`
      : '';
    return `<span class="${className}" data-tag="${escapeHtml(tagName)}"${escapedAttr}${missingAttr}${crossScopeAttr} contenteditable="false" style="${style}">${labelHtml}${warningHtml}</span>`;
  };

  const buildStorageMentionSpanHtml = (entry) => {
    const tooltip = t(
      'storage_mention.chip_tooltip',
      'Selected storage file: {{path}}',
      { label: entry.displayLabel, path: entry.path }
    );
    return `<span class="storage-mention-chip" data-storage-mention="true" data-storage-path="${escapeHtml(entry.path)}" contenteditable="false" title="${escapeHtml(tooltip)}" aria-label="${escapeHtml(tooltip)}"><span class="storage-mention-chip-label">@${escapeHtml(entry.displayLabel)}</span></span>`;
  };

  const buildUnresolvedStorageMentionSpanHtml = (mention) => {
    const tooltip = t(
      'storage_mention.unresolved_tooltip',
      'This file mention is not attached. Select the file from storage with @ before saving.'
    );
    return `<span class="storage-mention-chip storage-mention-unresolved" data-storage-mention="true" data-storage-unresolved="true" contenteditable="false" title="${escapeHtml(tooltip)}" aria-label="${escapeHtml(tooltip)}"><span class="storage-mention-chip-label">${escapeHtml(mention)}</span></span>`;
  };

  const renderStorageMentionsHtml = (text) => {
    if (!text) {
      return escapeHtml(text);
    }

    const entryByToken = new Map(
      storageMentionEntries.map((entry) => [`@${entry.label}`, entry])
    );
    const selectedMentionPattern = storageMentionEntries
      .map((entry) => escapeRegExp(`@${entry.label}`))
      .join('|');
    const matcher = new RegExp(
      [selectedMentionPattern, unresolvedStorageMentionPattern.source]
        .filter(Boolean)
        .join('|'),
      'gu'
    );
    let html = '';
    let lastIndex = 0;
    let match;
    while ((match = matcher.exec(text)) !== null) {
      if (match.index > lastIndex) {
        html += escapeHtml(text.substring(lastIndex, match.index));
      }
      const entry = entryByToken.get(match[0]);
      html += entry
        ? buildStorageMentionSpanHtml(entry)
        : buildUnresolvedStorageMentionSpanHtml(match[0]);
      lastIndex = matcher.lastIndex;
    }
    if (lastIndex < text.length) {
      html += escapeHtml(text.substring(lastIndex));
    }
    return html;
  };

  const closeTagActionMenu = useCallback(() => {
    setTagActionAnchorEl(null);
  }, []);

  const clearTagActionTarget = useCallback(() => {
    setTagActionTarget(null);
  }, []);

  const dispatchEditorInput = useCallback(() => {
    if (!rootRef.current) return;
    const event = new Event('input', { bubbles: true });
    rootRef.current.dispatchEvent(event);
  }, []);

  const focusCaretAfterNode = useCallback((node) => {
    if (!rootRef.current || !node) return;
    rootRef.current.focus();
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const replaceTagNode = (node, tagName, escaped = false) => {
    if (!node || !tagName) return;
    const updatedSpan = buildTagSpan(tagName, escaped);
    node.replaceWith(updatedSpan);
    focusCaretAfterNode(updatedSpan);
    dispatchEditorInput();
  };

  const removeTagNode = useCallback((node) => {
    if (!node) return;
    node.remove();
    if (rootRef.current) {
      rootRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(rootRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      dispatchEditorInput();
    }
  }, [dispatchEditorInput]);

  // Convert plain text (with `{tag}`) into HTML with span tags
  const toHtml = (text, options = {}) => {
    const { renderMissingTagsAsText = false } = options;
    const normalizedText = normalizeForMode(text);
    if (!normalizedText) return '';
    
    // First, handle newlines by converting them to <br> tags
    // We'll process line by line
    const lines = isSingleLine ? [normalizedText] : normalizedText.split('\n');
    let html = '';
    
    lines.forEach((line, lineIndex) => {
      tagRegex.lastIndex = 0;
      let lastIndex = 0;
      let match;
      
      while ((match = tagRegex.exec(line)) !== null) {
        const idx = match.index;
        if (idx > lastIndex) {
          html += renderStorageMentionsHtml(line.substring(lastIndex, idx));
        }
        const tagName = match[1] || match[2];
        const isEscaped = Boolean(match[1]);
        const isMissing = !isEscaped && isTagMissing(tagName);
        if (renderMissingTagsAsText && isMissing) {
          html += escapeHtml(match[0]);
        } else {
          html += buildTagSpanHtml(tagName, isEscaped);
        }
        lastIndex = tagRegex.lastIndex;
      }
      if (lastIndex < line.length) {
        html += renderStorageMentionsHtml(line.substring(lastIndex));
      }
      
      // Add <br> for newlines (except after the last line)
      if (lineIndex < lines.length - 1) {
        html += '<br>';
      }
    });
    
    return html;
  };

  const parseFromDom = (el) => {
    if (!el) return '';
    
    // Recursive function to properly handle nested elements and preserve newlines
    const extractText = (node) => {
      let result = '';
      
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.nodeValue;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();
        
        // Handle tag chips
        if (node.getAttribute && node.getAttribute('data-tag')) {
          const tagName = node.getAttribute('data-tag');
          const isEscaped = node.getAttribute('data-escaped') === 'true' || node.classList?.contains('tag-chip-escaped');
          return isEscaped ? `{{${tagName}}}` : `{${tagName}}`;
        }
        // Handle <br> elements - convert to newline
        else if (tagName === 'br') {
          result += '\n';
        }
        // Handle block elements (div, p) - add newline before content if not first
        else if (tagName === 'div' || tagName === 'p') {
          // Check if this div/p has actual content
          const hasContent = node.childNodes.length > 0;
          if (hasContent) {
            // Add newline before block element (except if it's the first child or previous sibling already added one)
            const prevSibling = node.previousSibling;
            const isFirstChild = !prevSibling;
            const prevEndsWithNewline = result.endsWith('\n');
            
            if (!isFirstChild && !prevEndsWithNewline) {
              result += '\n';
            }
            
            // Process children
            node.childNodes.forEach((child) => {
              result += extractText(child);
            });
          }
        }
        // Handle other elements - just process children
        else {
          node.childNodes.forEach((child) => {
            result += extractText(child);
          });
        }
      }
      
      return result;
    };
    
    let text = '';
    el.childNodes.forEach((node) => {
      text += extractText(node);
    });
    
    return normalizeForMode(text);
  };

  const getSelectedEditorText = () => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection || !selection.rangeCount) return '';
    const range = selection.getRangeAt(0);
    const startsInEditor = root.contains(range.startContainer);
    const endsInEditor = root.contains(range.endContainer);
    if (!startsInEditor || !endsInEditor) return '';

    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    return parseFromDom(wrapper);
  };

  const hasRawTagPattern = (el) => {
    if (!el) return false;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentNode && node.parentNode.closest && node.parentNode.closest('.tag-chip')) continue;
      if (tagPattern.test(node.nodeValue || '')) return true;
    }
    return false;
  };

  const getCaretOffset = () => {
    const root = rootRef.current;
    if (!root) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().length;
  };

  const restoreCaretFromMarker = () => {
    const root = rootRef.current;
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const value = node.nodeValue || '';
      const idx = value.indexOf(caretMarker);
      if (idx === -1) continue;
      const before = value.slice(0, idx);
      const after = value.slice(idx + caretMarker.length);
      node.nodeValue = before + after;
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.setStart(node, before.length);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    // Fallback: place caret at end
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  useEffect(() => {
    if (!rootRef.current) return;
    const currentText = parseFromDom(rootRef.current);
    const editorDefinitionSignature = `${tagLookupSignature}::storage:${storageMentionLookupSignature}`;
    const needToRebuild = currentText !== displayValue || tagDefinitionSignatureRef.current !== editorDefinitionSignature;
    if (needToRebuild) {
      const root = rootRef.current;
      const savedRange = savedSelectionRef.current;
      const editorHasFocus = root === document.activeElement || root.contains(document.activeElement);
      const preserveCaret = editorHasFocus && currentText === displayValue && savedRange?.collapsed
        && root.contains(savedRange.startContainer)
        && !savedRange.startContainer.parentElement?.closest('[contenteditable="false"]');
      let nextValue = displayValue;
      if (preserveCaret) {
        const marker = document.createTextNode(caretMarker);
        savedRange.cloneRange().insertNode(marker);
        nextValue = parseFromDom(root);
        marker.remove();
      }
      root.innerHTML = toHtml(nextValue);
      savedSelectionRef.current = null;
      if (preserveCaret) {
        restoreCaretFromMarker();
        const selection = window.getSelection();
        if (selection?.rangeCount) {
          savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
        }
      }
    }
    const currentStorageChipPaths = new Map();
    rootRef.current.querySelectorAll('[data-storage-mention="true"][data-storage-path]').forEach((chip) => {
      const path = chip.getAttribute('data-storage-path');
      if (path) {
        currentStorageChipPaths.set(path, (currentStorageChipPaths.get(path) || 0) + 1);
      }
    });
    storageChipPathsRef.current = currentStorageChipPaths;
    setIsEmpty(!hasVisibleEditorContent(rootRef.current, displayValue));
    tagDefinitionSignatureRef.current = editorDefinitionSignature;
  }, [displayValue, storageMentionLookupSignature, tagLookupSignature]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handleInput = () => {
      if (isDisabled) {
        return;
      }
      const text = parseFromDom(root);
      const currentStorageChipPaths = new Map();
      root.querySelectorAll('[data-storage-mention="true"][data-storage-path]').forEach((chip) => {
        const path = chip.getAttribute('data-storage-path');
        if (path) {
          currentStorageChipPaths.set(path, (currentStorageChipPaths.get(path) || 0) + 1);
        }
      });
      storageChipPathsRef.current.forEach((count, path) => {
        if (count > 0 && !currentStorageChipPaths.has(path)) {
          onStorageMentionRemove?.(path);
        }
      });
      storageChipPathsRef.current = currentStorageChipPaths;
      const normalizedText = isBlankText(text) ? '' : text;
      onChange && onChange(normalizedText);
      setIsEmpty(!hasVisibleEditorContent(root, normalizedText));

      if (isBlankText(normalizedText)) {
        if (root.innerHTML !== '') {
          root.innerHTML = '';
        }
        return;
      }

      if (hasRawTagPattern(root)) {
        const caretOffset = getCaretOffset();
        const safeOffset = typeof caretOffset === 'number'
          ? Math.min(Math.max(caretOffset, 0), normalizedText.length)
          : normalizedText.length;
        const markedText = `${normalizedText.slice(0, safeOffset)}${caretMarker}${normalizedText.slice(safeOffset)}`;
        const html = toHtml(markedText);
        if (root.innerHTML !== html) {
          root.innerHTML = html;
          restoreCaretFromMarker();
          const selection = window.getSelection();
          savedSelectionRef.current = selection?.rangeCount
            ? selection.getRangeAt(0).cloneRange() : null;
        }
      }
    };
    const handleClick = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const el = e.target.closest && e.target.closest('.tag-chip');
      if (el && el.dataset && el.dataset.tag) {
        e.preventDefault();
        e.stopPropagation();
        // Close any hover popover first
        setHoverAnchorEl(null);
        setHoveredTag(null);
        setTagActionAnchorEl(el);
        setTagActionTarget({
          tagName: el.dataset.tag,
          node: el,
          escaped: el.dataset.escaped === 'true'
        });
        return;
      }
      saveSelection();
    };
  const handleMouseOver = (e) => {
    if (isDisabled) {
      return;
    }
    if (tagActionAnchorEl || escapeDialogOpen || renameDialogOpen) {
      return;
    }
    if (e.target.closest && e.target.closest('.tag-chip-warning')) {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      setHoverAnchorEl(null);
      setHoveredTag(null);
      setHoverLoading(false);
      return;
    }
    const chip = e.target.closest && e.target.closest('.tag-chip');
    if (chip && chip.dataset && chip.dataset.tag) {
      const tagName = chip.dataset.tag;
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
          // Check cache first
          const cacheKey = `${caseid}:${tagName.toLowerCase()}`;
          const cached = tagPreviewCacheRef.current.get(cacheKey);
          if (cached) {
            setHoveredTag(cached);
            setHoverAnchorEl(chip);
            setHoverLoading(false);
            return;
          }
          // Request from backend
          if (caseid && sendMessage) {
            setHoverAnchorEl(chip);
            setHoveredTag({ tagName, loading: true });
            setHoverLoading(true);
            sendMessage({ command: 'getTagPreview', caseid, variable: tagName });
          }
        }, 400);
      } else {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      }
    };
  const handleMouseOut = (e) => {
    if (isDisabled) {
      return;
    }
      const chip = e.target.closest && e.target.closest('.tag-chip');
      if (!chip) {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        setHoverAnchorEl(null);
        setHoveredTag(null);
        setHoverLoading(false);
      }
    };
    const handlePaste = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      const clipboard = e.clipboardData || window.clipboardData;
      const files = Array.from(clipboard?.files || []);
      if (files.length && typeof onStorageFilesDrop === 'function') {
        e.preventDefault();
        void latestEditorApiRef.current.attachFiles(files);
        return;
      }
      const text = normalizeForMode(clipboard.getData('text'));
      const standaloneTag = getStandalonePastedTag(text);
      if (standaloneTag && (standaloneTag.escaped || !isTagMissing(standaloneTag.tagName))) {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;
        selection.deleteFromDocument();
        const range = selection.getRangeAt(0);
        const span = buildTagSpan(standaloneTag.tagName, standaloneTag.escaped);
        range.insertNode(span);
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        handleInput();
        return;
      }

      const pasteText = trimStandalonePastedTag(text);
      const html = toHtml(pasteText, { renderMissingTagsAsText: true });
      
      // Use insertHTML to preserve the tag chips structure
      if (document.queryCommandSupported('insertHTML')) {
        document.execCommand('insertHTML', false, html);
      } else {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        const div = document.createElement('div');
        div.innerHTML = html;
        const fragment = document.createDocumentFragment();
        while (div.firstChild) {
          fragment.appendChild(div.firstChild);
        }
        selection.getRangeAt(0).insertNode(fragment);
        selection.collapseToEnd();
      }
      handleInput();
    };
    const handleCopy = (e) => {
      if (isDisabled) {
        return;
      }
      const selectedText = getSelectedEditorText();
      if (!selectedText) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', selectedText);
    };
    const handleCut = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const selectedText = getSelectedEditorText();
      if (!selectedText) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', selectedText);
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      selection.deleteFromDocument();
      handleInput();
    };
    const removeAdjacentStorageChip = (direction, event) => {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return false;
      const range = selection.getRangeAt(0);
      if (!range.collapsed || !root.contains(range.startContainer)) return false;

      let chip = null;
      let spacer = null;
      const container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        const textLength = container.textContent?.length || 0;
        if (direction === 'backward' && range.startOffset === 0) {
          chip = container.previousSibling;
        } else if (
          direction === 'backward'
          && range.startOffset === textLength
          && !String(container.textContent || '').trim()
        ) {
          chip = container.previousSibling;
          spacer = container;
        } else if (direction === 'forward' && range.startOffset === textLength) {
          chip = container.nextSibling;
        } else if (direction === 'forward' && range.startOffset === 0) {
          chip = container.nextSibling;
        }
      } else if (container.nodeType === Node.ELEMENT_NODE) {
        const childIndex = direction === 'backward' ? range.startOffset - 1 : range.startOffset;
        chip = container.childNodes[childIndex];
      }

      if (!chip?.matches?.('[data-storage-mention="true"]')) return false;
      const parent = chip.parentNode;
      const before = chip.previousSibling;
      const after = chip.nextSibling;
      chip.remove();
      if (spacer?.parentNode === parent) spacer.remove();

      const nextRange = document.createRange();
      if (direction === 'backward' && before?.parentNode === parent && before.nodeType === Node.TEXT_NODE) {
        nextRange.setStart(before, before.textContent.length);
      } else if (direction === 'forward' && after?.parentNode === parent && after.nodeType === Node.TEXT_NODE) {
        nextRange.setStart(after, 0);
      } else {
        const anchorNode = direction === 'backward' ? after : before;
        const childIndex = anchorNode?.parentNode === parent
          ? Array.prototype.indexOf.call(parent.childNodes, anchorNode)
          : parent.childNodes.length;
        nextRange.setStart(parent, Math.max(0, childIndex));
      }
      nextRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(nextRange);
      savedSelectionRef.current = nextRange.cloneRange();
      event?.preventDefault?.();
      dispatchEditorInput();
      return true;
    };
    const handleKeyDown = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (!tagMenuAnchorEl) onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === 'Enter' && isSingleLine) {
        e.preventDefault();
      }

      saveSelection();

      if ((e.key === 'Backspace' || e.key === 'Delete') && removeAdjacentStorageChip(e.key === 'Backspace' ? 'backward' : 'forward', e)) {
        return;
      }
      
      // Open tag menu when typing '{'
      if (e.key === '{' && normalizedTags.length > 0 && !tagMenuAnchorEl) {
        // We use setTimeout to allow the '{' character to be inserted first,
        // then we capture the selection right after it.
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0 && rootRef.current && rootRef.current.contains(sel.anchorNode)) {
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
            rootRef.current.blur();
            setTagMenuAnchorEl(rootRef.current);
            setTriggeredByBrace(true);
          }
        }, 0);
      }

      // Storage mentions use the same prompt editor but are persisted as
      // validated file references, not as file content in the description.
      if (e.key === '@' && typeof onStorageMentionTrigger === 'function') {
        setTimeout(() => {
          saveSelection();
          onStorageMentionTrigger();
        }, 0);
      }
    };
    const handleBeforeInput = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (!isSingleLine) return;
      if (e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak') {
        e.preventDefault();
      }
    };

    const handleKeyUp = () => {
      saveSelection();
    };

    const handleMouseUp = () => {
      saveSelection();
    };

    // Save current selection state
    const saveSelection = () => {
       const sel = window.getSelection();
       if (sel && sel.rangeCount > 0 && root && root.contains(sel.anchorNode)) {
          savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
       }
    };
    
    root.addEventListener('input', handleInput);
    root.addEventListener('click', handleClick);
    root.addEventListener('paste', handlePaste);
    root.addEventListener('copy', handleCopy);
    root.addEventListener('cut', handleCut);
    root.addEventListener('keydown', handleKeyDown);
    root.addEventListener('keyup', handleKeyUp);
    root.addEventListener('mouseup', handleMouseUp);
    root.addEventListener('beforeinput', handleBeforeInput);
    root.addEventListener('mouseover', handleMouseOver);
    root.addEventListener('mouseout', handleMouseOut);
    return () => {
      root.removeEventListener('input', handleInput);
      root.removeEventListener('click', handleClick);
      root.removeEventListener('paste', handlePaste);
      root.removeEventListener('copy', handleCopy);
      root.removeEventListener('cut', handleCut);
      root.removeEventListener('keydown', handleKeyDown);
      root.removeEventListener('keyup', handleKeyUp);
      root.removeEventListener('mouseup', handleMouseUp);
      root.removeEventListener('beforeinput', handleBeforeInput);
      root.removeEventListener('mouseover', handleMouseOver);
      root.removeEventListener('mouseout', handleMouseOut);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, [onChange, onKeyDown, onStorageFilesDrop, onStorageMentionRemove, onStorageMentionTrigger, isSingleLine, normalizedTags.length, tagMenuAnchorEl, caseid, sendMessage, isDisabled]);

  // Subscribe to getTagPreview responses
  useEffect(() => {
    tagPreviewCacheRef.current.clear();
    setHoverAnchorEl(null);
    setHoveredTag(null);
  }, [caseid]);

  useEffect(() => {
    if (!subscribeToMessages) return;
    const unsubscribe = subscribeToMessages(['getTagPreview'], (message) => {
      const data = message.data || {};
      if (data.status !== 'success' || !data.variable || String(data.caseid) !== String(caseid)) return;
      const cacheKey = `${data.caseid}:${data.variable.toLowerCase()}`;
      const previewData = {
        tagName: data.variable,
        tag_type: data.tag_type,
        label: data.label || data.variable,
        content: data.content || '',
        type: String(data.type || 'text').trim().toLowerCase() || 'text',
        samples: data.samples || [],
        totalCount: data.totalCount || 0
      };
      tagPreviewCacheRef.current.set(cacheKey, previewData);
      // If we're still hovering on this tag, update the display
      setHoveredTag((prev) => {
        if (prev && prev.tagName && prev.tagName.toLowerCase() === data.variable.toLowerCase()) {
          setHoverLoading(false);
          return previewData;
        }
        return prev;
      });
    });
    return unsubscribe;
  }, [subscribeToMessages, caseid]);

  const closeEscapeDialog = () => {
    setEscapeDialogOpen(false);
    setEscapeTarget(null);
  };

  const openRenameDialog = () => {
    if (!tagActionTarget?.node || !tagActionTarget?.tagName) {
      closeTagActionMenu();
      return;
    }

    setRenameValue(tagActionTarget.tagName);
    setRenameError('');
    setRenameTarget(tagActionTarget);
    setRenameDialogOpen(true);
    setTagActionAnchorEl(null);
  };

  const closeRenameDialog = () => {
    setRenameDialogOpen(false);
    setRenameValue('');
    setRenameError('');
    setRenameTarget(null);
    setTagActionAnchorEl(null);
    setTagActionTarget(null);
  };

  const closeMissingTagDialog = useCallback(() => {
    setMissingTagDialogOpen(false);
    setMissingTagValue('');
    preserveBraceSelectionRef.current = false;
    setTagMenuAnchorEl(null);
    setTriggeredByBrace(false);
  }, []);

  const buildTagSpan = (tagName, escaped = false) => {
    const span = document.createElement('span');
    span.className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    span.setAttribute('data-tag', tagName);
    if (escaped) {
      span.setAttribute('data-escaped', 'true');
    }
    span.setAttribute('contenteditable', 'false');
    const color = generateSaturatedColor(tagName);
    if (escaped) {
      span.style.backgroundColor = 'transparent';
      span.style.color = color;
      span.style.border = `1px dashed ${color}`;
    } else {
      span.style.backgroundColor = generatePastelColor(tagName);
      span.style.color = color;
    }
    const missing = !escaped && isTagMissing(tagName);
    const crossScope = !escaped && !missing && isTagCrossScope(tagName);
    if (missing) {
      span.setAttribute('data-missing-tag', 'true');
    }
    if (crossScope) {
      span.setAttribute('data-cross-scope-tag', 'true');
    }
    const labelSpan = document.createElement('span');
    labelSpan.className = 'tag-chip-label';
    labelSpan.textContent = tagName;
    span.appendChild(labelSpan);
    if (missing || crossScope) {
      const warningText = missing ? getMissingTagTooltip(tagName) : getCrossScopeTagTooltip(tagName);
      const warningIcon = document.createElement('span');
      warningIcon.className = 'tag-chip-warning';
      if (crossScope) {
        warningIcon.classList.add('tag-chip-warning-cross-scope');
      }
      warningIcon.setAttribute('title', warningText);
      warningIcon.setAttribute('aria-label', warningText);
      warningIcon.setAttribute('data-warning-tooltip', warningText);
      warningIcon.textContent = '!';
      span.appendChild(warningIcon);
    }
    return span;
  };

  const buildStorageMentionSpan = (reference) => {
    const path = String(reference?.path || '').trim();
    const displayLabel = getStorageReferenceLabel(path);
    const tooltip = t(
      'storage_mention.chip_tooltip',
      'Selected storage file: {{path}}',
      { label: displayLabel, path }
    );
    const span = document.createElement('span');
    span.className = 'storage-mention-chip';
    span.setAttribute('data-storage-mention', 'true');
    span.setAttribute('data-storage-path', path);
    span.setAttribute('contenteditable', 'false');
    span.setAttribute('title', tooltip);
    span.setAttribute('aria-label', tooltip);
    const labelSpan = document.createElement('span');
    labelSpan.className = 'storage-mention-chip-label';
    labelSpan.textContent = `@${displayLabel}`;
    span.appendChild(labelSpan);
    return span;
  };

  const getStorageReferencesFromDom = () => {
    const root = rootRef.current;
    if (!root) return [];

    const references = [];
    const seenPaths = new Set();
    root.querySelectorAll('[data-storage-mention="true"][data-storage-path]').forEach((chip) => {
      const path = String(chip.getAttribute('data-storage-path') || '').trim();
      if (!path || seenPaths.has(path)) return;
      seenPaths.add(path);
      references.push({
        path,
        label: getStorageReferenceLabel(path),
      });
    });
    return references;
  };

  const getUnresolvedStorageMentionsFromDom = () => {
    const root = rootRef.current;
    if (!root) return [];

    const mentions = [];
    const seenMentions = new Set();
    root.querySelectorAll('[data-storage-unresolved="true"]').forEach((chip) => {
      const mention = String(
        chip.querySelector('.storage-mention-chip-label')?.textContent || chip.textContent || ''
      ).trim();
      if (!mention || seenMentions.has(mention)) return;
      seenMentions.add(mention);
      mentions.push(mention);
    });
    return mentions;
  };

  const handleEscapeConfirm = () => {
    if (!escapeTarget?.node || !escapeTarget?.tagName) {
      closeEscapeDialog();
      return;
    }

    const { tagName, node, escaped } = escapeTarget;
    replaceTagNode(node, tagName, !escaped);
    closeEscapeDialog();
  };

  const handleDeleteTag = () => {
    if (!tagActionTarget?.node) {
      closeTagActionMenu();
      return;
    }

    removeTagNode(tagActionTarget.node);
    clearTagActionTarget();
    closeTagActionMenu();
  };

  const handleRenameTag = () => {
    if (!tagActionTarget?.node || !tagActionTarget?.tagName) {
      closeTagActionMenu();
      return;
    }

    openRenameDialog();
  };

  const handleRenameConfirm = () => {
    const nextTagName = String(renameValue || '').trim();
    if (!renameTarget?.node || !renameTarget?.tagName) {
      closeRenameDialog();
      return;
    }

    if (!nextTagName || !tagNamePattern.test(nextTagName) || hasUserTagVariableReservedChars(nextTagName)) {
      setRenameError(t('tag_editor.rename_invalid', 'Tag names can only contain letters, numbers, and underscores, and cannot use reserved metadata suffixes.'));
      return;
    }

    replaceTagNode(renameTarget.node, nextTagName, Boolean(renameTarget.escaped));
    closeRenameDialog();
  };

  const insertTagAtCursor = (tagName) => {
    if (isDisabled) {
      return;
    }
    const sel = window.getSelection();
    if (!sel || !sel.getRangeAt || !rootRef.current) return;
    
    let range;
    
    // Check if current selection is valid and inside editor
    if (sel.rangeCount > 0 && rootRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      range = sel.getRangeAt(0);
    }
    // If not, try to use saved selection
    else if (savedSelectionRef.current) {
       range = savedSelectionRef.current;
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    // If still no valid range inside editor, fallback to end
    if (!range || !rootRef.current.contains(range.commonAncestorContainer)) {
       rootRef.current.focus();
       range = document.createRange();
       range.selectNodeContents(rootRef.current);
       range.collapse(false);
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    const span = buildTagSpan(tagName);
    range.deleteContents();
    range.insertNode(span);
    // Position caret after the inserted text node
    const newRange = document.createRange();
    newRange.setStartAfter(span);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    
    // Trigger change - manually dispatch input event so handleInput runs
    if (rootRef.current) {
      const event = new Event('input', { bubbles: true });
      rootRef.current.dispatchEvent(event);
    }
  };

  const insertStorageMentionAtCursor = (reference, { replaceMentionTrigger = false, preserveText = false } = {}) => {
    if (isDisabled || !rootRef.current || !reference?.path) return;
    const root = rootRef.current;
    const selection = window.getSelection();
    let range = savedSelectionRef.current ? savedSelectionRef.current.cloneRange() : null;
    if (!range && selection && selection.rangeCount > 0 && root.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0).cloneRange();
    }
    if (!range || !root.contains(range.commonAncestorContainer)) {
      root.focus();
      range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
    }

    // A description can contain a file-looking mention restored from an older
    // widget without its structured reference. Selecting that exact file is
    // an explicit user action, so upgrade the unresolved chip in place instead
    // of appending a second copy elsewhere in the editor.
    if (preserveText) range.collapse(false);
    const selectedStorageLabel = `@${getStorageReferenceLabel(reference.path)}`;
    const selectedStorageFileName = `@${getStorageFileName(reference.path)}`;
    const unresolvedChip = Array.from(
      root.querySelectorAll('[data-storage-unresolved="true"]')
    ).find((chip) => {
      const chipText = String(
        chip.querySelector('.storage-mention-chip-label')?.textContent
          || chip.textContent
          || ''
      ).trim();
      return chipText === selectedStorageLabel || chipText === selectedStorageFileName;
    });
    if (unresolvedChip) {
      range = document.createRange();
      range.selectNode(unresolvedChip);
    }

    if (replaceMentionTrigger && range.collapsed && range.startContainer?.nodeType === Node.TEXT_NODE) {
      const textBeforeCaret = range.startContainer.textContent?.[range.startOffset - 1];
      if (textBeforeCaret === '@') {
        range.setStart(range.startContainer, range.startOffset - 1);
      }
    }

    range.deleteContents();
    const span = buildStorageMentionSpan(reference);
    range.insertNode(span);
    let spacer = span.nextSibling;
    if (!(spacer?.nodeType === Node.TEXT_NODE && /\s/.test(spacer.textContent || ''))) {
      spacer = document.createTextNode(' ');
      span.parentNode?.insertBefore(spacer, span.nextSibling);
    }
    const nextRange = document.createRange();
    nextRange.setStartAfter(spacer || span);
    nextRange.collapse(true);
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
    savedSelectionRef.current = nextRange.cloneRange();
    root.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const insertTextAtCursor = (text, { replaceMentionTrigger = false } = {}) => {
    if (isDisabled || !rootRef.current) return;
    const root = rootRef.current;
    const selection = window.getSelection();
    let range = savedSelectionRef.current ? savedSelectionRef.current.cloneRange() : null;
    if (!range && selection && selection.rangeCount > 0 && root.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0).cloneRange();
    }
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
    }

    if (replaceMentionTrigger && range.collapsed && range.startContainer?.nodeType === Node.TEXT_NODE) {
      const textBeforeCaret = range.startContainer.textContent?.[range.startOffset - 1];
      if (textBeforeCaret === '@') {
        range.setStart(range.startContainer, range.startOffset - 1);
      }
    }

    range.deleteContents();
    const textNode = document.createTextNode(String(text || ''));
    range.insertNode(textNode);
    const nextRange = document.createRange();
    nextRange.setStartAfter(textNode);
    nextRange.collapse(true);
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
    savedSelectionRef.current = nextRange.cloneRange();
    root.dispatchEvent(new Event('input', { bubbles: true }));
  };

  // Keep imperative callers connected to the current render without forcing
  // insertion callbacks to be recreated in parent actions.
  latestEditorApiRef.current = {
    insertTagAtCursor,
    insertTextAtCursor,
    insertStorageMentionAtCursor,
    parseFromDom,
    getStorageReferencesFromDom,
    getUnresolvedStorageMentionsFromDom
  };
  useImperativeHandle(ref, () => ({
    insertTag: (tagName) => latestEditorApiRef.current.insertTagAtCursor(tagName),
    insertText: (text, options) => latestEditorApiRef.current.insertTextAtCursor(text, options),
    insertStorageMention: (reference, options) => latestEditorApiRef.current.insertStorageMentionAtCursor(reference, options),
    focus: () => !isDisabled && rootRef.current && rootRef.current.focus(),
    getValue: () => latestEditorApiRef.current.parseFromDom(rootRef.current),
    getStorageReferences: () => latestEditorApiRef.current.getStorageReferencesFromDom(),
    getTagReferences: () => [...new Set(Array.from(rootRef.current?.querySelectorAll('.tag-chip') || [])
      .filter(chip => chip.dataset.escaped !== 'true').map(chip => chip.dataset.tag).filter(Boolean))],
    removeStorageMention: (path) => {
      rootRef.current?.querySelectorAll('.storage-mention-chip').forEach(chip => {
        if (chip.dataset.storagePath === path) chip.remove();
      });
      rootRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
    },
    getUnresolvedStorageMentions: () => latestEditorApiRef.current.getUnresolvedStorageMentionsFromDom(),
    hasPendingAttachments: () => pendingUploadRef.current
  }), [isDisabled]);

  const removeLeadingBraceForTriggeredSelection = useCallback(() => {
    if (!triggeredByBrace || !savedSelectionRef.current) {
      return;
    }

    try {
      const sel = window.getSelection();
      if (!sel) {
        return;
      }
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);

      const range = sel.getRangeAt(0);
      if (range.startOffset > 0) {
        range.setStart(range.startContainer, range.startOffset - 1);
        range.deleteContents();
      }
    } catch (err) {
      console.warn('Failed to remove brace', err);
    } finally {
      setTriggeredByBrace(false);
    }
  }, [triggeredByBrace]);

  const insertResolvedTag = useCallback((tagName) => {
    if (!tagName) {
      closeMissingTagDialog();
      return;
    }

    removeLeadingBraceForTriggeredSelection();
    latestEditorApiRef.current.insertTagAtCursor(tagName);
    setTagMenuAnchorEl(null);
    closeMissingTagDialog();
  }, [closeMissingTagDialog, removeLeadingBraceForTriggeredSelection]);

  useEffect(() => {
    if (tagActionAnchorEl || escapeDialogOpen || renameDialogOpen) {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      setHoverAnchorEl(null);
      setHoveredTag(null);
      setHoverLoading(false);
    }
  }, [tagActionAnchorEl, escapeDialogOpen, renameDialogOpen]);

  const handleInternalTagClick = (event) => {
    setTagMenuAnchorEl(event.currentTarget);
  };

  const handleInternalTagSelect = (tagValue) => {
    insertResolvedTag(tagValue);
  };

  // TagsDropdown always provides the standard system tags, even when the action
  // has no custom tags. Keep the insert control available in that case.
  const showTagButton = !isDisabled;
  const canAttachFiles = !isDisabled && typeof onStorageFilesDrop === 'function';
  const attachFiles = async (files) => {
    if (!canAttachFiles || pendingUploadRef.current || !files.length) return;
    pendingUploadRef.current = true;
    setAttachmentError(false);
    try {
      await onStorageFilesDrop(files);
    } catch (error) {
      setAttachmentError(true);
    } finally {
      pendingUploadRef.current = false;
    }
  };
  latestEditorApiRef.current.attachFiles = attachFiles;
  const saveAttachmentPosition = (event) => {
    const root = rootRef.current;
    const pointRange = document.caretRangeFromPoint?.(event.clientX, event.clientY);
    if (pointRange && root?.contains(pointRange.startContainer)) {
      const chip = pointRange.startContainer.parentElement?.closest('[contenteditable="false"]');
      if (chip) pointRange.setStartAfter(chip);
      pointRange.collapse(true);
      savedSelectionRef.current = pointRange.cloneRange();
    } else {
      const selection = window.getSelection();
      if (selection?.rangeCount && root?.contains(selection.anchorNode)) {
        savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
      }
    }
  };

  return (
    <Box data-file-drop-target="true" sx={{ position: 'relative', width: '100%', minWidth: 0 }}
      onDragEnter={(event) => {
        if (!hasFileDragData(event)) return;
        event.preventDefault(); event.stopPropagation();
        dragDepthRef.current += 1;
        if (canAttachFiles) setFileDragActive(true);
      }}
      onDragOver={(event) => {
        if (!hasFileDragData(event)) return;
        event.preventDefault(); event.stopPropagation();
        event.dataTransfer.dropEffect = canAttachFiles ? 'copy' : 'none';
      }}
      onDragLeave={(event) => {
        if (!hasFileDragData(event)) return;
        event.preventDefault(); event.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (!dragDepthRef.current) setFileDragActive(false);
      }}
      onDrop={(event) => {
        if (!hasFileDragData(event)) return;
        event.preventDefault(); event.stopPropagation();
        dragDepthRef.current = 0;
        setFileDragActive(false);
        saveAttachmentPosition(event);
        void attachFiles(Array.from(event.dataTransfer.files || []));
      }}>
      <Box
        component="div"
        ref={rootRef}
        id={id}
        role="textbox"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-disabled={isDisabled || undefined}
        aria-multiline={!isSingleLine}
        className={className}
        contentEditable={!isDisabled}
        suppressContentEditableWarning
        spellCheck={!isDisabled}
        sx={{
        width: '100%',
        minWidth: 0,
        minHeight: computedMinHeight,
        maxHeight: computedMaxHeight,
        borderRadius: '12px',
        border: '1px solid #ddd',
        backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
        color: isDisabled ? 'text.disabled' : 'text.primary',
        cursor: isDisabled ? 'not-allowed' : 'text',
        p: editorPadding,
        pr: canAttachFiles ? 9 : showTagButton ? 5 : editorPadding,
        '&:focus': { outline: isDisabled ? 'none' : `2px solid rgba(87,87,176,0.1)` },
        whiteSpace: isSingleLine ? 'nowrap' : 'pre-wrap',
        overflowX: isSingleLine ? 'auto' : 'hidden',
        overflowY: isSingleLine ? 'hidden' : 'auto',
        '& .tag-chip': {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'monospace',
          fontSize: '0.75rem'
        },
        '& .tag-chip[data-missing-tag="true"], & .tag-chip[data-cross-scope-tag="true"]': {
          paddingRight: '14px'
        },
        '& .tag-chip-escaped': {
          backgroundColor: 'transparent'
        },
        '& .tag-chip-label': {
          display: 'inline-block'
        },
        '& .storage-mention-chip': {
          display: 'inline-flex',
          alignItems: 'center',
          maxWidth: 'min(100%, 420px)',
          padding: '2px 8px',
          borderRadius: '12px',
          border: '1px solid rgba(25, 118, 210, 0.42)',
          backgroundColor: 'rgba(25, 118, 210, 0.10)',
          color: '#1565c0',
          fontSize: '0.75rem',
          fontWeight: 600,
          marginRight: '6px',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          cursor: 'default',
          '&::before': {
            content: '"📎"',
            marginRight: '4px',
            fontSize: '0.8rem',
          }
        },
        '& .storage-mention-unresolved': {
          border: '1px dashed rgba(237, 108, 2, 0.78)',
          backgroundColor: 'rgba(237, 108, 2, 0.10)',
          color: '#a15c00',
          cursor: 'help',
          '&::before': {
            content: '"⚠"',
            marginRight: '4px',
            fontSize: '0.8rem',
          }
        },
        '& .storage-mention-chip-label': {
          display: 'inline-block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .tag-chip-warning': {
          position: 'absolute',
          top: '-4px',
          right: '-4px',
          width: '18px',
          height: '18px',
          borderRadius: '999px',
          backgroundColor: '#d32f2f',
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
          letterSpacing: '0.05em',
          zIndex: 2,
          '&::after': {
            content: 'attr(data-warning-tooltip)',
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 280,
            maxWidth: 'min(280px, calc(100vw - 24px))',
            padding: '8px 10px',
            borderRadius: '8px',
            backgroundColor: '#263238',
            color: '#fff',
            fontFamily: 'inherit',
            fontSize: '0.72rem',
            fontWeight: 400,
            lineHeight: 1.35,
            letterSpacing: 'normal',
            textAlign: 'left',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            boxShadow: '0 3px 12px rgba(0,0,0,0.22)',
            pointerEvents: 'none',
            opacity: 0,
            visibility: 'hidden',
            transform: 'translateY(-3px)',
            transition: 'opacity 120ms ease, transform 120ms ease, visibility 120ms ease',
          },
          '&:hover::after, &:focus-visible::after': {
            opacity: 1,
            visibility: 'visible',
            transform: 'translateY(0)',
          }
        },
        '& .tag-chip-warning-cross-scope': {
          backgroundColor: '#ed6c02'
        }
      }}
    />
      {canAttachFiles && <>
        <input ref={fileInputRef} type="file" multiple hidden onChange={(event) => {
          const files = Array.from(event.target.files || []);
          event.target.value = '';
          void attachFiles(files);
        }} />
        <Tooltip title={t('storage_mention.attach_local')}>
          <IconButton size="small" aria-label={t('storage_mention.attach_local')}
            onMouseDown={saveAttachmentPosition} onClick={() => fileInputRef.current?.click()}
            sx={{ position: 'absolute', right: 40, top: isSingleLine ? '50%' : 8,
              transform: isSingleLine ? 'translateY(-50%)' : 'none', zIndex: 1 }}>
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>}
      {fileDragActive && canAttachFiles && <Box role="status" sx={{
        position: 'absolute', inset: 0, border: '2px dashed', borderColor: 'primary.main',
        borderRadius: '12px', bgcolor: 'background.paper', color: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        pointerEvents: 'none', zIndex: 2, p: 1,
      }}><AttachFileIcon fontSize="small" /><Typography variant="body2">{t('storage_mention.drop_here')}</Typography></Box>}
      {attachmentError && <Typography variant="caption" color="error" role="alert">
        {t('storage_mention.upload_error')}
      </Typography>}
      {isEmpty && placeholder && (
        <Box
          onClick={() => {
            if (!isDisabled && rootRef.current) rootRef.current.focus();
          }}
          sx={{
            position: 'absolute',
            left: 12,
            top: isSingleLine ? 8 : 12,
            pointerEvents: isDisabled ? 'none' : 'auto',
            color: isDisabled ? 'text.disabled' : 'rgba(0,0,0,0.38)',
            fontSize: '0.95rem',
            userSelect: 'none',
            cursor: isDisabled ? 'default' : 'text'
          }}
        >{placeholder}</Box>
      )}
      {showTagButton && (
        <Tooltip title={t('action_common.insert_tag_tooltip', 'Insert a dynamic tag')}>
          <IconButton
            onMouseDown={(e) => {
              // Try to prevent focus loss from editor when clicking button
              e.preventDefault();
            }}
            onClick={onTagClick || handleInternalTagClick}
            aria-label={t('action_common.insert_tag_tooltip', 'Insert a dynamic tag')}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: isSingleLine ? '50%' : 8,
              transform: isSingleLine ? 'translateY(-50%)' : 'none',
              color: 'action.active',
              zIndex: 1
            }}
          >
            <LocalOfferIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {showTagButton && (
        <TagsDropdown
          tags={normalizedTags}
          includeDashboardTags={includeDashboardTags}
          includeSystemTags={includeSystemTags}
          handleTagSelect={handleInternalTagSelect}
          onMissingTagSelect={(tagValue) => {
            if (!tagValue) return;
            preserveBraceSelectionRef.current = true;
            setMissingTagValue(tagValue);
            setMissingTagDialogOpen(true);
          }}
          anchorEl={tagMenuAnchorEl}
          setAnchorEl={(el) => {
            setTagMenuAnchorEl(el);
            if (!el && !preserveBraceSelectionRef.current) {
              setTriggeredByBrace(false);
              rootRef.current?.focus();
            }
          }}
          scope={scope}
        />
      )}
      <Menu
        anchorEl={tagActionAnchorEl}
        open={Boolean(tagActionAnchorEl && tagActionTarget)}
        onClose={() => {
          closeTagActionMenu();
          setTagActionTarget(null);
        }}
        disableAutoFocusItem
      >
        <MenuItem
          onClick={handleDeleteTag}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {referenceOnly ? t('project_chat.references.remove_tag') : t('tag_editor.delete_tag', 'Delete tag')}
        </MenuItem>
        <MenuItem
          onClick={handleRenameTag}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {referenceOnly ? t('project_chat.references.change_tag') : t('tag_editor.rename_tag', 'Rename tag')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!tagActionTarget?.node) {
              closeTagActionMenu();
              return;
            }
            setEscapeTarget(tagActionTarget);
            setEscapeDialogOpen(true);
            closeTagActionMenu();
          }}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <TextFieldsIcon fontSize="small" />
          </ListItemIcon>
          {tagActionTarget?.escaped
            ? t('tag_editor.convert_to_tag', 'Convert to tag')
            : t('tag_editor.convert_to_text', 'Convert to text')}
        </MenuItem>
      </Menu>
      <Dialog open={escapeDialogOpen} onClose={closeEscapeDialog}
        TransitionProps={{ onExited: () => { if (active && !disabled) rootRef.current?.focus(); } }}>
        <DialogTitle>
          {escapeTarget?.escaped
            ? t('tag_editor.unescape_title', 'Convert back to a tag?')
            : t('tag_editor.escape_title', 'Keep this tag as plain text?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_body', 'This will turn the plain text back into a real tag so it can be replaced with data.')
              : t('tag_editor.escape_body', 'If you choose “Keep as Text,” this tag will stay exactly as you typed it and will not be replaced with real data.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEscapeDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleEscapeConfirm} variant="contained">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_confirm', 'Convert to Tag')
              : t('tag_editor.escape_confirm', 'Keep as Text')}
          </Button>
          </DialogActions>
      </Dialog>
      <Dialog open={renameDialogOpen} onClose={closeRenameDialog} fullWidth maxWidth="xs"
        TransitionProps={{ onExited: () => { if (active && !disabled) rootRef.current?.focus(); } }}>
        <DialogTitle>
          {t('tag_editor.rename_dialog_title', 'Rename tag')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('tag_editor.rename_dialog_label', 'New tag name')}
            value={renameValue}
            onChange={(event) => {
              setRenameValue(event.target.value);
              if (renameError) setRenameError('');
            }}
            error={Boolean(renameError)}
            helperText={renameError || t('tag_editor.rename_dialog_helper', 'Use letters, numbers, and underscores. Reserved metadata suffixes are not allowed.')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleRenameConfirm} variant="contained">
            {t('tag_editor.rename_confirm', 'Rename')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={missingTagDialogOpen} onClose={closeMissingTagDialog} fullWidth maxWidth="xs"
        TransitionProps={{ onExited: () => { if (active && !disabled) rootRef.current?.focus(); } }}>
        <DialogTitle>
          {t('tag_editor.missing_tag_dialog_title', 'Tag does not exist')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('tag_editor.missing_tag_dialog_body', 'This tag does not exist. Do you want to insert it anyway?')}
          </Typography>
          <Box sx={{ mt: 1.5, p: 1, borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.03)', fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {`{${missingTagValue}}`}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMissingTagDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            autoFocus
            onClick={() => insertResolvedTag(missingTagValue)}
            variant="contained"
          >
            {t('tag_editor.missing_tag_dialog_confirm', 'Insert anyway')}
          </Button>
        </DialogActions>
      </Dialog>
      <TagPreviewPopover
        anchorEl={hoverAnchorEl}
        tag={hoveredTag}
        loading={hoverLoading}
        onClose={() => { setHoverAnchorEl(null); setHoveredTag(null); setHoverLoading(false); }}
        onMouseLeave={() => { setHoverAnchorEl(null); setHoveredTag(null); setHoverLoading(false); }}
      />
    </Box>
  );
});

export default TagEditor;
